'use client';

import { useState, useCallback, useEffect, useRef } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { HomeRes } from '@/types/api/hall/home.d';
import clsx from 'clsx';
import DotButton from '../../components/dot-button';
import './index.css';
import { CardBannerItem, CardType } from '../../services/home';
import { track } from '@/utils/track';
import { setWithdrawBannerSource, setDepositBannerSource } from '@/utils/banner-source';
import type { WithdrawRequestSource } from '@/utils/track';
import { useThrottleByRef } from '../../hooks/use-throttle-expose';
import { BannerAutoPlayProps } from '../../const/game';
import ImageLoad from '../../components/image-load';
import { useInViewport } from 'ahooks';
import { useModalOverlay } from '../../hooks/use-modal-overlay';
import { useRouter } from '@/i18n/navigation';
import { jumpByUrl, isJumpAction, parseJumpAction } from '@/utils/jump-url';
import { fantasyGameUrlAtom } from '@/app/[locale]/(main)/(non-activity)/fantasy/store';
import { useAtom, useAtomValue } from 'jotai';
import { isLoginAtom } from '@/store';
import { JUMP_ACTION } from '@/const/jump-action';
interface BannerProps {
  banners: HomeRes['top_banner'];
  className?: string;
  from?: string;
  module?: CardType;
  source?: WithdrawRequestSource;
}

export default function Banner(props: BannerProps) {
  const { banners = [], className, from = 'Home', source } = props;
  const isLogin = useAtomValue(isLoginAtom);

  const router = useRouter();
  const [_, setFantasyGameUrl] = useAtom(fantasyGameUrlAtom);

  const [currentSlide, setCurrentSlide] = useState(0);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    loop: true,
  }, [
    Autoplay(BannerAutoPlayProps)
  ]);
  const postTrackRef = useRef<boolean | undefined>(undefined);
  const bannerWrapperRef = useRef<HTMLDivElement | null>(null);
  const [isBannerInViewport] = useInViewport(bannerWrapperRef);
  const hasModalOverlay = useModalOverlay();

  const handleBannerShow = (item: CardBannerItem, index: number) => {
    if (!postTrackRef.current) return;
    track('banner_exposure', {
      current_page: from,
      current_module: item?.module_name,
      banner_number: index + 1,
      to_url: item?.link || '',
    });
  };
  // 调用 Hook，传入上报函数和等待时间
  // 第一次调用会立即执行，20s 后可以再次执行
  const bannerShow = useThrottleByRef({
    wait: 20 * 1000,
  });

  const goToSlide = useCallback(
    (index: number) => {
      if (emblaApi) {
        emblaApi.scrollTo(index);
      }
    },
    [emblaApi]
  );

  // 预加载所有内部链接的 banner 页面
  useEffect(() => {
    if (banners && banners.length > 0) {
      banners.forEach((item) => {
        const { link } = item || {};
        const itemWithDetail = item as CardBannerItem;
        // 只预加载内部链接
        if (link && !/^https?:\/\//i.test(link)) {
          router.prefetch(link);
        } else if (itemWithDetail.has_activity_common_detail) {
          router.prefetch(`/banner-detail?id=${item.uuid}&type=1`);
        }
      });
    }
  }, [banners, router]);

  // 监听slide变化
  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      const newSlide = emblaApi.selectedScrollSnap();
      setCurrentSlide((prev) => {
        if (banners?.[prev]?.uuid && postTrackRef.current) {
          bannerShow(banners[prev].uuid, () => {
            handleBannerShow(banners[prev] as CardBannerItem, prev);
          });
        }
        return newSlide;
      });
    };

    emblaApi.on('select', onSelect);
    onSelect(); // 初始化时设置当前slide

    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, banners, bannerShow]);

  const handleClick = (item: CardBannerItem, index: number) => {
    const { link, has_activity_common_detail } = item || {};
    console.log('banner_click');
    track('banner_click', {
      current_page: from,
      current_module: item?.module_name,
      banner_number: index + 1,
      to_url: link,
    });
    if (source && link && isJumpAction(link) && isLogin) {
      const { action } = parseJumpAction(link) || {};
      if (action === JUMP_ACTION.OPEN_WITHDRAW_CASH || action === JUMP_ACTION.OPEN_WITHDRAW_CRYPTO) {
        setWithdrawBannerSource(source);
      }
      if (action === JUMP_ACTION.OPEN_DEPOSIT_CASH) {
        setDepositBannerSource(source);
      }
    }
    if (link) {
      setFantasyGameUrl('');
      jumpByUrl(link, router, item?.module_name);
    } else if (has_activity_common_detail) {
      router.push(`/banner-detail?id=${item.uuid}&type=1`);
    }
  };

  useEffect(() => {
    // 只有当 banner 在视口中且没有弹窗遮挡时才允许上报
    const canTrack = !!isBannerInViewport && !hasModalOverlay;
    const prevCanTrack = postTrackRef.current;
    postTrackRef.current = canTrack;

    // 当只有一张图且从不可上报变为可上报时，触发上报
    if (!prevCanTrack && canTrack && banners?.length === 1 && banners[0]?.uuid) {
      bannerShow(banners[0].uuid, () => {
        handleBannerShow(banners[0] as CardBannerItem, 0);
      });
    }
  }, [isBannerInViewport, hasModalOverlay, banners, bannerShow]);

  return (
    <>
      {
        banners?.length > 0 && (
          <div className={clsx('relative px-[12px] mx-auto !mt-[10px]', className)} ref={bannerWrapperRef}>

            {/* 轮播图容器 */}
            <div className="embla relative overflow-hidden">
              <div className="embla__viewport h-full" ref={emblaRef}>
                <div className="embla__container flex h-full ml-[-12px]">
                  {banners?.map((obj, index) => {
                    const { link } = obj || {};
                    const itemWithDetail = obj as CardBannerItem;
                    const isExternalLink = link && /^https?:\/\//i.test(link);
                    const internalLink = !isExternalLink && link ? link :
                      itemWithDetail.has_activity_common_detail ? `/banner-detail?id=${obj.uuid}&type=1` : null;

                    // 预加载banner详情页
                    if (internalLink) {
                      router.prefetch(internalLink);
                    }

                    const imageElement = obj?.img ? (
                      <ImageLoad
                        skeletonClassName="absolute inset-0"
                        src={obj?.img || ''}
                        alt={`Banner ${index + 1}`}
                        className="w-full h-full object-fill"
                        height={100}
                        width={360}
                        onClick={(e) => {
                          e?.stopPropagation();
                          handleClick(obj as CardBannerItem, index);
                        }}
                      />
                    ) : (
                      <div className={clsx('w-full h-ful')} />
                    );

                    return (
                      <div key={index} className="embla__slide w-full pl-[12px]">
                        <div className="aspect-[360/120] overflow-hidden rounded-[10px] relative h-full w-full flex items-center justify-center click-effect-block">
                          {/* 此处用Link组件跳转会引起页面刷新，换成手动预加载 */}
                          {/* {internalLink ? (
                            <Link
                              href={internalLink}
                              prefetch={true}
                              className="w-full h-full block"
                              onClick={(e) => {
                                e.preventDefault();
                                handleClick(obj as CardBannerItem, index);
                              }}
                            >
                              {imageElement}
                            </Link>
                          ) : (
                            imageElement
                          )} */}
                          {imageElement}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* 指示器 */}
              {emblaApi && banners?.length > 1 && (
                <div className="absolute bottom-[10px] left-[15px] z-10">
                  <DotButton
                    totalSlides={banners?.length || 0}
                    currentSlide={currentSlide}
                    onSlideChange={goToSlide}
                  />
                </div>
              )}
            </div>
          </div>
        )
      }
    </>
  );
}